// ── Home page ──────────────────────────────────────────────────────────────

export const heroMetrics = [
  {
    label: "PCN-aligned operations",
    value: "Licensed",
    detail: "Built around compliant pharmaceutical wholesale workflows.",
  },
  {
    label: "Supply focus",
    value: "B2B Wholesale",
    detail: "Structured for pharmacies, clinics, hospitals, and distributors.",
  },
  {
    label: "Core catalogue",
    value: "Generics + OTC",
    detail: "Includes branded products and parallel imports.",
  },
  {
    label: "Market focus",
    value: "Trade Accounts",
    detail: "Structured for repeat supply to pharmacies, hospitals, and distributors.",
  },
];

export const trustPoints = [
  "Wholesale fulfilment for pharmacies, clinics, hospitals, and resellers.",
  "Structured product handling across fast-moving medicines and healthcare lines.",
  "Professional support for trade enquiries, product availability, and repeat supply.",
];

export const homeServices = [
  {
    icon: "💊",
    title: "Wholesale Medicine Supply",
    text: "High-volume fulfilment for community pharmacies, hospitals, specialist clinics, and regional distributors.",
  },
  {
    icon: "🌍",
    title: "Parallel Import Sourcing",
    text: "Category support for imported pharmaceutical lines where buyers need breadth, continuity, and competitive pricing.",
  },
  {
    icon: "🏥",
    title: "OTC and Front-of-Shop Lines",
    text: "Consumer healthcare, wellness, and high-turnover over-the-counter products for retail and institutional buyers.",
  },
  {
    icon: "🔄",
    title: "Account-Based Replenishment",
    text: "Designed for repeat wholesale relationships with responsive account handling and dependable supply follow-through.",
  },
];

export const homeStats = [
  { value: "4+", label: "Product Categories" },
  { value: "B2B", label: "Trade Focus" },
  { value: "PCN", label: "Licensed & Compliant" },
  { value: "24h", label: "Enquiry Response" },
];

// ── About page ─────────────────────────────────────────────────────────────

export const aboutIntro = {
  eyebrow: "Who We Are",
  heading: "A pharmaceutical wholesaler built for the modern trade buyer.",
  body: "Etiquette Pharmaceuticals Limited is a PCN-licensed wholesale distributor operating from Abuja, Nigeria. We supply generics, branded medicines, OTC products, and parallel imports to pharmacies, hospitals, clinics, and regional distributors who need dependable stock and responsive account support.",
};

export const companyHighlights = [
  {
    title: "Trade Buyer Focus",
    text: "Built for pharmacies, hospitals, clinics, and distributors that need dependable wholesale supply and responsive account support.",
  },
  {
    title: "Structured Product Portfolio",
    text: "Generics, branded medicines, OTC products, and parallel imports are grouped clearly so procurement teams can review supply options faster.",
  },
  {
    title: "Operational Visibility",
    text: "Clear communication, product organisation, and dependable account handling support stronger wholesale operations.",
  },
];

export const missionValues = [
  {
    number: "01",
    title: "Compliance First",
    text: "Every product we handle moves through PCN-aligned workflows. Regulatory compliance is not optional — it is the foundation of every transaction.",
  },
  {
    number: "02",
    title: "Buyer-Centred Service",
    text: "We structure our operations around what trade buyers actually need: clear product availability, fast responses, and reliable fulfilment.",
  },
  {
    number: "03",
    title: "Long-Term Relationships",
    text: "We invest in account continuity. Repeat buyers receive consistent service, product guidance, and proactive supply communication.",
  },
  {
    number: "04",
    title: "Transparent Operations",
    text: "From enquiry to dispatch, buyers know where their order stands. No ambiguity in stock position, pricing, or delivery timelines.",
  },
];

export const licenseInfo = {
  heading: "Regulatory Standing",
  body: "Etiquette Pharmaceuticals Limited operates under a valid Pharmaceutical Council of Nigeria (PCN) wholesale licence. Our operations are structured to meet the standards required for pharmaceutical distribution in Nigeria, including product traceability, storage compliance, and documentation integrity.",
  points: [
    "PCN-licensed wholesale distributor",
    "Registered in Nigeria: RC 1727250",
    "Compliant storage and handling procedures",
    "Full documentation trail from supplier to buyer",
  ],
};

// ── Products page ──────────────────────────────────────────────────────────

export const productCategories = [
  {
    id: "generics",
    name: "Generics",
    tag: "Prescription",
    description: "Fast-moving prescription medicines for core pharmacy demand. Our generics portfolio covers the most frequently dispensed therapeutic categories.",
    items: ["Antibiotics", "Antihypertensives", "Antidiabetics", "Analgesics", "Antiparasitics", "Vitamins & Supplements"],
  },
  {
    id: "branded",
    name: "Branded Medicines",
    tag: "Prescription & OTC",
    description: "Recognized branded lines for institutional and retail accounts. We supply original manufacturer products with full documentation.",
    items: ["Branded Antibiotics", "Branded Cardiovascular", "Branded Respiratory", "Branded Gastrointestinal", "Branded Dermatology", "Branded Paediatrics"],
  },
  {
    id: "otc",
    name: "Over-the-Counter",
    tag: "OTC",
    description: "OTC treatments, wellness products, and high-frequency retail products for front-of-shop and institutional buyers.",
    items: ["Pain Relief", "Cold & Flu", "Digestive Health", "Skin Care", "Eye & Ear Care", "Vitamins & Minerals"],
  },
  {
    id: "parallel",
    name: "Parallel Imports",
    tag: "Import",
    description: "Imported lines that broaden buyer choice and commercial flexibility. All parallel imports are sourced through compliant channels.",
    items: ["European Generics", "Asian Generics", "Specialty Medicines", "Rare Therapeutics", "Biosimilars", "Nutraceuticals"],
  },
  {
    id: "consumables",
    name: "Consumables",
    tag: "Healthcare",
    description: "Selected healthcare essentials that support wholesale account baskets and institutional procurement needs.",
    items: ["Surgical Gloves", "Syringes & Needles", "Wound Care", "Diagnostic Strips", "PPE", "Infusion Sets"],
  },
  {
    id: "seasonal",
    name: "Seasonal Demand Lines",
    tag: "Seasonal",
    description: "Products positioned for demand spikes, replenishment windows, and campaign periods throughout the year.",
    items: ["Malaria Season Lines", "Harmattan Respiratory", "Back-to-School Health", "Festive Wellness Packs", "Rainy Season Antiparasitics", "Vitamin D Lines"],
  },
];

export const productFAQ = [
  {
    q: "How do I check product availability?",
    a: "Submit a trade enquiry through our contact form or call our account team directly. We confirm stock position, batch availability, and substitution options within 24 hours.",
  },
  {
    q: "Do you supply to individual pharmacies?",
    a: "Yes. We work with independent community pharmacies, retail chains, hospitals, clinics, and regional distributors. All buyers must hold a valid pharmacy or healthcare licence.",
  },
  {
    q: "What is your minimum order quantity?",
    a: "Minimum order quantities vary by product category. Contact our wholesale team for category-specific MOQ information and pricing tiers.",
  },
  {
    q: "Can you source products not listed in your catalogue?",
    a: "Yes. Our parallel import and sourcing capability means we can often fulfil requests outside our standard catalogue. Speak with our account team about specific requirements.",
  },
];

// ── Services page ──────────────────────────────────────────────────────────

export const serviceCards = [
  {
    number: "01",
    title: "Wholesale Medicine Supply",
    text: "High-volume fulfilment for community pharmacies, hospitals, specialist clinics, and regional distributors. We handle picking, invoice preparation, and dispatch scheduling.",
    detail: "Our wholesale supply service is built for buyers who need consistent stock across multiple product lines. We manage availability checks, batch documentation, and fulfilment coordination so your procurement team can focus on patient care and business operations.",
  },
  {
    number: "02",
    title: "Parallel Import Sourcing",
    text: "Category support for imported pharmaceutical lines where buyers need breadth, continuity, and competitive pricing.",
    detail: "We source parallel imports through compliant channels, providing buyers with access to European and Asian generics, specialty medicines, and biosimilars that may not be available through standard domestic supply chains.",
  },
  {
    number: "03",
    title: "OTC and Front-of-Shop Lines",
    text: "Consumer healthcare, wellness, and high-turnover over-the-counter products for retail and institutional buyers.",
    detail: "Our OTC portfolio is structured for high-frequency replenishment. We supply pain relief, cold and flu, digestive health, skin care, and vitamins to pharmacies and healthcare retailers who need reliable stock of fast-moving consumer lines.",
  },
  {
    number: "04",
    title: "Account-Based Replenishment",
    text: "Designed for repeat wholesale relationships with responsive account handling and dependable supply follow-through.",
    detail: "Trade accounts receive dedicated support, consistent pricing, and proactive communication on stock availability. We build long-term supply relationships with buyers who need predictable fulfilment and a single point of contact.",
  },
];

export const processSteps = [
  {
    step: "01",
    title: "Account Enquiry",
    text: "Buyers submit product needs, organisation details, and preferred turnaround times via our contact form or by phone.",
  },
  {
    step: "02",
    title: "Availability Check",
    text: "The team confirms stock position, batch availability, and substitution options where needed — within 24 hours.",
  },
  {
    step: "03",
    title: "Wholesale Fulfilment",
    text: "Orders move through picking, invoice preparation, and dispatch scheduling with full documentation.",
  },
  {
    step: "04",
    title: "Ongoing Account Support",
    text: "Repeat buyers receive continuity, product guidance, and responsive follow-up for future orders.",
  },
];

export const qualityPillars = [
  "Clear account contact points for trade buyers and supply managers.",
  "Professional handling of pharmaceutical and healthcare product enquiries.",
  "Structured wholesale workflows from product request to dispatch planning.",
  "Focus on continuity, responsiveness, and long-term customer relationships.",
];

export const wholesaleAccounts = [
  {
    customer: "Retail Pharmacy Chains",
    note: "Repeat replenishment of fast-moving generics and OTC lines.",
  },
  {
    customer: "Hospitals and Clinics",
    note: "Structured ordering with product continuity and predictable fulfilment.",
  },
  {
    customer: "Regional Distributors",
    note: "Higher-volume basket orders across multiple medicine categories.",
  },
];

// ── Contact page ───────────────────────────────────────────────────────────

export const contactInfo = {
  phone: "+234 806 129 8497",
  email: "info@etiquettepharmaceuticals.com",
  address: "House 12-14, S. Zakari Close, NAF Valley Estate, Asokoro, Abuja",
  hours: "Monday – Friday: 8:00am – 5:00pm WAT",
};

export const contactReasons = [
  "Monthly wholesale supply",
  "Urgent replenishment",
  "Parallel import sourcing",
  "New account onboarding",
  "Product availability enquiry",
  "Other",
];
